import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Shield, 
  Calendar,
  ArrowRight
} from 'lucide-react';
import TestimonialsCarousel from './TestimonialsCarousel';
import '../styles/ClientsSection.css';

const ClientsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });



  const industries = [
    { 
      name: 'E-commerce', 
      count: 120, 
      icon: '🛒',
      description: 'Automatización completa para tiendas online',
      hoverMessage: 'Automatiza consultas de productos, procesa pedidos, integra con Shopify/WooCommerce, recupera carritos abandonados y brinda atención 24/7.',
      color: '#7AB55C'
    },
    { 
      name: 'Salud', 
      count: 85, 
      icon: '🏥',
      description: 'Gestión inteligente de citas y pacientes',
      hoverMessage: 'Agenda citas automáticamente, envía recordatorios, gestiona historiales clínicos básicos y confirma consultas médicas.',
      color: '#4CAF50'
    },
    { 
      name: 'Inmobiliaria', 
      count: 95, 
      icon: '🏠',
      description: 'Calificación y seguimiento de leads',
      hoverMessage: 'Califica leads automáticamente, programa visitas, envía catálogos personalizados y calcula créditos hipotecarios.',
      color: '#FF9800'
    },
    { 
      name: 'Restaurantes', 
      count: 70, 
      icon: '🍽️',
      description: 'Reservas y pedidos automatizados',
      hoverMessage: 'Automatiza reservas de mesas, ofrece menú digital, gestiona delivery y recolecta feedback de clientes.',
      color: '#FF6B35'
    },
    { 
      name: 'Educación', 
      count: 60, 
      icon: '📚',
      description: 'Inscripciones y comunicación con padres',
      hoverMessage: 'Automatiza inscripciones, envía recordatorios de clases, gestiona pagos y mantiene comunicación con padres.',
      color: '#3F51B5'
    },
    { 
      name: 'Automotriz', 
      count: 45, 
      icon: '🚗',
      description: 'Servicios y mantenimiento programado',
      hoverMessage: 'Agenda servicios automáticamente, consulta repuestos, cotiza reparaciones y gestiona garantías.',
      color: '#607D8B'
    },
    { 
      name: 'Tecnología', 
      count: 35, 
      icon: '💻',
      description: 'Soporte técnico automatizado',
      hoverMessage: 'Brinda soporte nivel 1, agenda consultorías, gestiona tickets y programa demostraciones de productos.',
      color: '#2196F3'
    },
    { 
      name: 'Marketing Digital', 
      count: 55, 
      icon: '📱',
      description: 'Automatización para agencias y community managers',
      hoverMessage: 'Gestiona múltiples clientes, automatiza respuestas en redes sociales, genera reportes de engagement, califica leads desde social media y coordina campañas multicanal.',
      color: '#E91E63'
    },
    { 
      name: 'Otros', 
      count: 90, 
      icon: '🏢',
      description: 'Soluciones personalizadas por industria',
      hoverMessage: 'Desarrollamos automatizaciones personalizadas, integramos con APIs existentes y creamos flujos específicos para tu negocio.',
      color: '#9C27B0'
    }
  ];

  return (
    <section id="clients" className="clients-section" ref={ref}>
      <div className="container">
        {/* Header mejorado */}
        <motion.div 
          className="section-header text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="trust-badge">
            <Shield className="badge-icon" size={18} />
            <span>Empresa Verificada</span>
          </div>
          <h2>Más de <span className="text-gradient">500 Empresas</span> Confían en Runamatic</h2>
          <p className="section-subtitle">
          Desde startups hasta grandes empresas, organizaciones líderes en toda 
          Latinoamérica confían en Runamatic para automatizar su atención al cliente.
          </p>
        </motion.div>



        {/* Industrias que atendemos */}
        <motion.div 
          className="industries-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h3>Industrias que Atendemos</h3>
          <p className="industries-subtitle">
            Pasa el cursor sobre cualquier industria para descubrir cómo Runamatic puede transformar tu negocio
          </p>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                className="industry-card"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.03 }}
              >
                <div className="industry-header">
                  <div className="industry-logo">
                    <div className="industry-icon">{industry.icon}</div>
                  </div>
                  <div className="industry-info">
                    <h4 className="industry-name">{industry.name}</h4>
                    <span className="industry-count">{industry.count}+ empresas</span>
                  </div>
                </div>
                
                <p className="industry-description">{industry.description}</p>
                
                <div className="industry-status">
                  <span className="status-indicator" style={{ backgroundColor: industry.color }}></span>
                  <span className="status-text">Disponible</span>
                </div>

                <div className="industry-overlay">
                  <div className="overlay-content">
                    <h4 className="overlay-title">{industry.name}</h4>
                    <p className="overlay-description">{industry.hoverMessage}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Sección de Testimonios */}
        <TestimonialsCarousel />

        {/* CTA súper mejorado */}
        <motion.div 
          className="clients-cta runamatic-premium"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="cta-content">
            <div className="cta-badge">
              <span>Únete a Más de 500 Empresas Exitosas</span>
            </div>
            
            <h3>
              Comienza Tu 
              <span className="cta-highlight"> Transformación Digital </span>
              Hoy
            </h3>
            <p>
              Más de 500 empresas ya optimizaron su atención al cliente con Runamatic. 
              Descubre por qué eligen nuestra plataforma para automatizar sus operaciones.
            </p>
            
            <div className="cta-buttons">
              <motion.a
                href="https://app.runamatic.io/en/login"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary-new"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Comenzar Prueba Gratuita</span>
                <ArrowRight size={18} />
              </motion.a>
              
              <motion.a
                href="https://calendar.app.google/JeF9eEUM5uRNcXvz7"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-demo-super"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Agendar Demo Personalizada</span>
                <Calendar size={18} />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientsSection; 
